import { HOUSEHOLD_DOC, MEMBER_COLORS, type HouseholdMember } from '@awwyeah/shared';
import { useMemo } from 'react';
import { useYDoc, useYMap } from '../../sync';
import { useUser } from '../auth';

export interface RosterMember {
  id: string;
  name: string;
  color: string;
  avatarEmoji?: string;
}

/**
 * Own copy of `apps/web/src/modules/lists/useHouseholdRoster.ts` — feature modules may not
 * import each other's internals (docs/ARCHITECTURE.md), only a foundation module's published
 * `index.ts`. Same small-hook-duplication precedent as `lists`/`apps`/`games`.
 */

/** Deterministic fallback color for a member id we have no profile for yet. */
function fallbackColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return MEMBER_COLORS[hash % MEMBER_COLORS.length] as string;
}

/** Household member roster for the recipe box's "added by" filter chips. Merges whatever
 * profiles are published to the `household` doc with the signed-in user, falling back to a
 * deterministic color + "Member" label for anyone else (recipes created before the roster
 * existed, or by someone whose profile hasn't synced yet). */
export function useHouseholdRoster(): {
  members: Map<string, RosterMember>;
  meId: string | null;
  getMember: (id: string) => RosterMember;
} {
  const { user } = useUser();
  const doc = useYDoc(HOUSEHOLD_DOC);
  const { entries } = useYMap<HouseholdMember>(doc, 'members');

  const members = useMemo(() => {
    const map = new Map<string, RosterMember>();
    for (const [id, member] of entries) {
      map.set(id, {
        id,
        name: member.name,
        color: member.color,
        avatarEmoji: member.avatarEmoji,
      });
    }
    if (user && !map.has(user.id)) {
      map.set(user.id, {
        id: user.id,
        name: user.name,
        color: user.color,
        avatarEmoji: user.avatarEmoji,
      });
    }
    return map;
  }, [entries, user]);

  const getMember = (id: string): RosterMember =>
    members.get(id) ?? {
      id,
      name: id === user?.id ? user.name : 'Member',
      color: fallbackColor(id),
    };

  return { members, meId: user?.id ?? null, getMember };
}
